import React from 'react';
import type { RuntimePolicyOption } from '../../generated/api/types.gen.js';
import { Badge } from './Badge.js';

interface RuntimePolicyOptionListProps {
  options: readonly RuntimePolicyOption[];
  /** Selected policy id; null selects the default option. */
  value: number | null;
  onChange: (policyId: number) => void;
  disabled?: boolean;
  /** Unique radio group name. */
  name: string;
}

/** Presentational radio list: each option shows its name, a default badge and its models. */
export const RuntimePolicyOptionList: React.FC<RuntimePolicyOptionListProps> = ({
  options,
  value,
  onChange,
  disabled = false,
  name,
}) => {
  const selectedId = value ?? options.find((o) => o.isDefault)?.id ?? null;
  return (
    <div role="radiogroup" aria-label="Runtime policy" className="flex flex-col gap-2">
      {options.map((option) => {
        const selected = option.id === selectedId;
        return (
          <label
            key={option.id}
            className={`flex items-start gap-2.5 rounded-lg border px-3 py-2 text-sm ${
              selected ? 'border-accent-text bg-accent-muted' : 'border bg-surface hover:bg-surface-hover'
            } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <input
              type="radio"
              name={name}
              value={option.id}
              checked={selected}
              disabled={disabled}
              onChange={() => onChange(option.id)}
              className="mt-1"
            />
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="flex items-center gap-2 text-heading font-medium">
                {option.name}
                {option.isDefault && <Badge variant="success">default</Badge>}
              </span>
              {option.description && <span className="text-xs text-muted">{option.description}</span>}
              <span className="text-xs text-faint">
                {option.models.length > 0 ? option.models.map((m) => m.displayName).join(' → ') : 'No models'}
              </span>
            </span>
          </label>
        );
      })}
    </div>
  );
};
